import React, { useContext, useEffect, useState } from 'react';
import { currency, getFormattedNumber } from '@ecg-internal/aurora-js-formatters';

import EnvironmentContext from '../../contexts/EnvironmentContext';
import CarsSimpleSearch from './components/CarsSimpleSearch';
import getCarsSearchFields from './repositories/getCarsSearchFields';
import { trackGAEvent } from '../../utils/gaTracking';
import transformForForm from './utils/transformForForm';
import {
  VEHICLE_TYPE_CATEGORY_IDS,
  VEHICLE_TYPE_CATEGORY_IDS_NUM,
  VEHICLE_TYPE_EV,
  VEHICLE_TYPES,
} from '../../../../constants/vehicleTypes';
import { ATTRIBUTE_VALUES } from '../../../../constants/attributes';
import isPageReloaded from './utils/isPageReloaded';
import { CARS_L1_CATEGORY_ID } from '../../constants/categories';
import { getSimpleSearchFields } from './constants/simpleSeachFields';
import CarsContinueSearch from './components/CarsContinueSearch';

import './CarsSearch.module.scss';

import { TCategoryItemApi } from 'types/TCategories';
import { TCarsSearchFieldsClient } from 'types/TCarsSearchFields';
import VehicleNavigationBar from '../Motors/components/VehicleNavigationBar';
import Classes from '../CarsPageContent/components/CarsPageContent.module.scss';

type TCarsSearchProps = {
  l1Category: TCategoryItemApi;
  vehicleType: string;
  carsSearchFields: TCarsSearchFieldsClient;
  userPostcode: string;
  priceType: string;
  intent?: string;
};

const L2_CATEGORY_FIELD = 'l2CategoryId';
const OFFERED_SINCE_FIELD = 'offeredSince';
const LOCAL_STORAGE_KEY = 'lastSearches';
const FIXED_FORM_FIELD_KEYS = new Set([L2_CATEGORY_FIELD, 'model']);
const EV_FUEL_TYPES = new Set(['11756', '13838', '13839']);

let CarsAdvancedSearch;

const CarsSearchApp = ({
  l1Category,
  vehicleType,
  carsSearchFields,
  userPostcode,
  priceType,
  intent,
}: TCarsSearchProps) => {
  const {
    links: { wwwDomain },
    experiments: { isReplaceStateEnabled },
  } = useContext(EnvironmentContext);
  const [formFields, setFormFields] = useState({
    [VEHICLE_TYPES.CARS]: { ...carsSearchFields },
    [VEHICLE_TYPES.VANS]: { ...carsSearchFields },
    [VEHICLE_TYPES.OLDTIMERS]: { ...carsSearchFields },
    [VEHICLE_TYPES.TRUCKS]: { ...carsSearchFields },
    [VEHICLE_TYPE_EV]: { ...carsSearchFields },
  });
  const initialForm = {
    l2CategoryId: '',
    attributes: {
      model: undefined,
    },
    rangeAttributes: {},
    query: '',
    offeredSince: '',
    distance: '',
    postcode: userPostcode || '',
  };
  const priceDefault = {
    attributes: {
      model: undefined,
      priceType: [priceType],
    },
  };
  const evDefault = {
    attributes: {
      model: undefined,
      priceType: [priceType],
      fuel: [ATTRIBUTE_VALUES.FUEL.ELECTRIC],
    },
  };
  const initialFormState = {
    [VEHICLE_TYPES.CARS]: { ...initialForm, ...priceDefault },
    [VEHICLE_TYPES.VANS]: { ...initialForm, l2CategoryId: VEHICLE_TYPE_CATEGORY_IDS.VANS, ...priceDefault },
    [VEHICLE_TYPES.OLDTIMERS]: { ...initialForm, l2CategoryId: VEHICLE_TYPE_CATEGORY_IDS.OLDTIMERS },
    [VEHICLE_TYPES.TRUCKS]: { ...initialForm, l2CategoryId: VEHICLE_TYPE_CATEGORY_IDS.TRUCKS },
    [VEHICLE_TYPE_EV]: { ...initialForm, ...evDefault },
  };
  const [formState, setFormState] = useState(initialFormState);
  const [brandState, setBrandState] = useState('');
  const [resultCount, setResultCount] = useState();
  const [advancedSearchIsOpen, setAdvancedSearchIsOpen] = useState(false);
  const [lastSearch, setLastSearch] = useState<TSearchEntry>();
  const [lastRelevantSearch, setLastRelevantSearch] = useState<boolean>(false);

  const toggleAdvancedSearch = async (event) => {
    event.preventDefault();
    setFormState({
      ...formState,
      [vehicleType]: transformForForm(formState[vehicleType], advancedSearchIsOpen, formFields[vehicleType]),
    });
    if (!advancedSearchIsOpen) {
      trackGAEvent('showSearchAdvanced');
    }
    try {
      CarsAdvancedSearch = (
        await import(/* webpackChunkName: "CarsAdvancedSearch" */ './components/CarsAdvancedSearch')
      ).default;
      setAdvancedSearchIsOpen(!advancedSearchIsOpen);
    } catch {}
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    let link, filtersCount;

    try {
      const createLinks = (await import(/* webpackChunkName: "createLinks" */ '../../utils/linkHelpers')).default;
      ({ link, filtersCount } = createLinks({
        l1Category,
        formFields: formFields[vehicleType],
        formState: formState[vehicleType],
      }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error creating link', e);
    }
    const formType = advancedSearchIsOpen ? 'Advanced' : 'Simple';
    if (filtersCount) {
      trackGAEvent(`formSubmitted${formType}`, filtersCount.toString());
    }
    trackGAEvent(`searchSubmitted`, vehicleType);

    const isFuelTypeElectric = formState[vehicleType].attributes?.['fuel']
      ? formState[vehicleType].attributes?.['fuel']?.some((value) => EV_FUEL_TYPES.has(value))
      : false;
    if (isFuelTypeElectric) {
      trackGAEvent(`electricFuelSubmitted`, formState[vehicleType].attributes?.['fuel'].join());
    }

    if (formState[vehicleType].attributes?.['priceType']) {
      trackGAEvent('priceTypeSubmit', formState[vehicleType].attributes?.['priceType'].join());
    }

    // Using history API as it's part of the navigation history and doesn't require to handle invalidation of the state
    if (isReplaceStateEnabled) {
      window.history.replaceState(formState, '', window.location.href);
    }
    window.location.assign(`${window.location.protocol}//${wwwDomain}${link}`);
  };

  const onReset = () => {
    setFormState({ ...formState, [vehicleType]: initialFormState[vehicleType] });
    setBrandState('');
  };

  const handleChange = (name, value, type, singleSelect) => {
    const fieldName = name.split('.')[0]; // Needed for range attributes
    const formField = formFields[vehicleType][fieldName];
    const updatedFormState = { ...formState[vehicleType] };

    if (type === 'AttributeGroupFacet' && fieldName !== OFFERED_SINCE_FIELD) {
      let updatedAttribute = formState[vehicleType].attributes[name];
      const isAttributeSet = updatedAttribute;

      if (!formField) {
        return;
      }

      if (singleSelect || !isAttributeSet) {
        // replace values
        updatedAttribute = Array.isArray(value) ? value : [value];
      } else {
        // toggle only value from args
        const index = updatedAttribute.indexOf(value);
        if (index >= 0) {
          updatedAttribute.splice(index, 1);
        } else {
          updatedAttribute.push(value);
        }
      }
      // Ensure both BUY & LEASE selected if priceType becomes empty or contains only ''
      if (
        fieldName === 'priceType' &&
        (!updatedAttribute ||
          updatedAttribute.length === 0 ||
          (updatedAttribute.length === 1 && updatedAttribute[0] === ''))
      ) {
        updatedAttribute = [ATTRIBUTE_VALUES.PRICE_TYPE.BUY, ATTRIBUTE_VALUES.PRICE_TYPE.LEASE];
      }

      updatedFormState.attributes = {
        ...formState[vehicleType]['attributes'],
        [name]: updatedAttribute,
      };
    } else if (type === 'AttributeRangeFacet') {
      const rangeAttributes = { ...formState[vehicleType]['rangeAttributes'] };
      const subField = name.split('.')[1];
      const existingValue = rangeAttributes[fieldName];

      if (existingValue) {
        const updatedAttribute = existingValue;
        updatedAttribute[subField] = value;
        rangeAttributes[fieldName] = updatedAttribute;
      } else {
        const updatedAttribute = {
          attributeKey: fieldName,
        };
        updatedAttribute[subField] = value;
        rangeAttributes[fieldName] = updatedAttribute;
      }

      updatedFormState.rangeAttributes = rangeAttributes;
    } else {
      updatedFormState[name] = value;
    }

    if (name === L2_CATEGORY_FIELD) {
      const formFieldsWithoutModel = { ...formFields[vehicleType] };
      delete formFieldsWithoutModel.model;

      setBrandState(value);
      setFormFields({ ...formFields, [vehicleType]: formFieldsWithoutModel });

      if (updatedFormState.attributes.model) {
        delete updatedFormState.attributes.model;
      }
    }

    setFormState({ ...formState, [vehicleType]: updatedFormState });
  };

  const handleRemoveLastSearch = (event) => {
    event.preventDefault();
    trackGAEvent('Removed', 'continue_search');
    try {
      const localStorageValue = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localStorageValue) {
        const parsedValue = JSON.parse(localStorageValue);
        const lastSearchesWithoutCars = parsedValue.filter((entry) => entry.l1CategoryId !== CARS_L1_CATEGORY_ID);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(lastSearchesWithoutCars));
        setLastSearch(undefined);
      }
    } catch {}
  };

  useEffect(() => {
    void (async () => {
      const formStateArg = formState[vehicleType];
      const searchFields = await getCarsSearchFields(brandState, formStateArg);

      if (searchFields && searchFields.facets && Object.keys(searchFields.facets).length) {
        const facets = { ...searchFields.facets };
        const updatedFormFields = { ...formFields[vehicleType], ...facets };

        Object.keys(formFields[vehicleType]).forEach((formFieldKey) => {
          if (!facets[formFieldKey] && !FIXED_FORM_FIELD_KEYS.has(formFieldKey)) {
            delete updatedFormFields[formFieldKey];
          }
        });

        setFormFields({
          ...formFields,
          [vehicleType]: updatedFormFields,
        });
      }

      if (searchFields.totalResultCount) {
        setResultCount(searchFields.totalResultCount);
      }
    })();
  }, [formState, vehicleType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (vehicleType === VEHICLE_TYPE_EV) {
      setBrandState('');
    }
  }, [vehicleType]);

  useEffect(() => {
    const priceTypeState = formState[VEHICLE_TYPES.CARS]['attributes']['priceType'];
    const priceCentsState = formState[VEHICLE_TYPES.CARS]['rangeAttributes']['PriceCents'];
    const formFieldsWithLeasePrices = { ...formFields };
    const priceCentsField = formFieldsWithLeasePrices[VEHICLE_TYPES.CARS].PriceCents;

    if (priceCentsField && priceTypeState) {
      if (priceTypeState.length === 1 && priceTypeState[0] === ATTRIBUTE_VALUES.PRICE_TYPE.LEASE) {
        priceCentsField.label = priceCentsField.leaseLabel;
        if (priceCentsField.leaseOptions) {
          priceCentsField.options = priceCentsField.leaseOptions;
        }
        setFormFields(formFieldsWithLeasePrices);
      } else {
        priceCentsField.label = priceCentsField.buyLabel;
        if (priceCentsField.buyOptions) {
          priceCentsField.options = priceCentsField.buyOptions;
        }
      }

      if (
        priceCentsState &&
        !advancedSearchIsOpen &&
        !priceCentsField?.options?.find((option) => option.value === priceCentsState?.to)
      ) {
        delete formState[VEHICLE_TYPES.CARS]['rangeAttributes']['PriceCents'];
      }
    }
  }, [formFields.cars.priceType, formState, advancedSearchIsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isReplaceStateEnabled && window?.history?.state && !isPageReloaded()) {
      setFormState(window?.history?.state);
    }
  }, [isReplaceStateEnabled]);

  useEffect(() => {
    try {
      const localStorageValue = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localStorageValue) {
        const parsedValue = JSON.parse(localStorageValue);
        const carsLastSearch = parsedValue.filter((entry) => entry.l1CategoryId === CARS_L1_CATEGORY_ID);

        if (carsLastSearch.length) {
          const { search, attributeLabels } = carsLastSearch[0];
          const priceRangeFilter = search.attributeRanges.find(({ attributeKey }) => attributeKey === 'PriceCents')?.[
            'to'
          ];
          const mileageRangeFilter = search.attributeRanges.find(({ attributeKey }) => attributeKey === 'mileage')?.[
            'to'
          ];
          const constructionYearRangeFilter = search.attributeRanges.find(
            ({ attributeKey }) => attributeKey === 'constructionYear',
          )?.['from'];
          Object.keys(attributeLabels).forEach((attributeLabelKey) => {
            const attributeLabel = attributeLabels[attributeLabelKey];
            if (['PriceCents', 'mileage', 'constructionYear'].includes(attributeLabel.facetId)) {
              delete attributeLabels[attributeLabelKey];
            }
          });

          void (async (): Promise<void> => {
            try {
              const { lrpUrl } = await import(
                /* webpackChunkName: "lrpUrl" */ '@ecg-marktplaats/aurora-node-url-builder'
              );
              setLastSearch({
                l2CategoryId: search.categories.l2Category?.id,
                l2CategoryName: search.categories.l2Category?.fullName,
                priceRangeFilter: priceRangeFilter ? currency(priceRangeFilter, true, true) : priceRangeFilter,
                constructionYearRangeFilter,
                mileageRangeFilter: mileageRangeFilter ? getFormattedNumber(mileageRangeFilter) : mileageRangeFilter,
                filterCount: search.attributesById.length,
                attributeLabels,
                attributesById: search.attributesById,
                link: lrpUrl({
                  categories: search.categories,
                  attributes: search.attributes,
                  rangeAttributes: search.attributeRanges,
                  distance: search.distance?.distanceMeters,
                  postcode: search.distance?.postcode,
                  view: search.viewOptions?.kind,
                  query: search.searchQuery,
                  attributesByKey: search.attributesByKey,
                }),
              });
            } catch {}
          })();
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!lastSearch) {
      return;
    }

    const matchesL2Category =
      vehicleType === VEHICLE_TYPES.CARS || vehicleType === VEHICLE_TYPE_EV
        ? !Object.values(VEHICLE_TYPE_CATEGORY_IDS_NUM).includes(lastSearch.l2CategoryId)
        : lastSearch.l2CategoryId === VEHICLE_TYPE_CATEGORY_IDS_NUM[vehicleType];

    const matchesFuelType =
      vehicleType !== VEHICLE_TYPE_EV ||
      Boolean(lastSearch.attributesById.filter((attr) => EV_FUEL_TYPES.has(attr.toString())).length);

    const matchesPriceType = lastSearch.attributesById.includes(Number(priceType));

    setLastRelevantSearch(matchesL2Category && matchesPriceType && matchesFuelType);
  }, [lastSearch, vehicleType, priceType]);

  return (
    <>
      <div className={`PageContent-card ${Classes.heroSearch}`}>
        <VehicleNavigationBar />
        <CarsSimpleSearch
          formFields={formFields[vehicleType]}
          onChange={handleChange}
          onSubmit={onSubmit}
          toggleAdvancedSearch={toggleAdvancedSearch}
          resultCount={resultCount}
          formState={formState[vehicleType]}
          lastSearch={lastRelevantSearch ? lastSearch : undefined}
          onRemoveLastSearch={handleRemoveLastSearch}
          simpleSearchKeys={getSimpleSearchFields(vehicleType, intent)}
        />
      </div>

      {advancedSearchIsOpen && (
        <CarsAdvancedSearch
          formFields={formFields[vehicleType]}
          onChange={handleChange}
          onSubmit={onSubmit}
          toggleAdvancedSearch={toggleAdvancedSearch}
          resultCount={resultCount}
          formState={formState[vehicleType]}
          onReset={onReset}
        />
      )}

      {lastSearch && lastRelevantSearch && (
        <CarsContinueSearch searchEntry={lastSearch} asCard onRemoveLastSearch={handleRemoveLastSearch} />
      )}
    </>
  );
};

export default CarsSearchApp;
